import Close16 from '@carbon/icons-react/es/close/16';
import Upload16 from '@carbon/icons-react/es/upload/16';
import { Upload } from 'antd';
import Dragger from 'antd/lib/upload/Dragger';
import { UploadFile } from 'antd/lib/upload/interface';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectFileGroup } from 'api/project-files/types';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { Radio } from 'components/atoms/radio';
import { toastService } from 'components/molecules/toast';
import { TaskFilterButton } from 'components/molecules/views/components';
import { HideIfNotAllowed } from 'components/organisms/permission-controller';
import { PermissionRuleName } from 'constant';
import { useTranslate } from 'hooks/use-translation';
import { formatBytes } from 'utils/files';
import { MAX_FILE_SIZE_LIMIT } from './constants';
import { FileExtensionIcon } from './file-extension-icon';

const ModalTitle = styled.div`
  margin-bottom: var(--spacing-04);
`;

const StyledDragger = styled(Dragger)`
  text-align: left;
  padding: var(--spacing-04) var(--spacing-09);
`;

const DraggerTitle = styled.p`
  font-size: var(--heading-6-size);
  margin-bottom: var(--spacing-05);
`;

const DraggerOption = styled.p`
  color: var(--gray-40);
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-04);
`;

const StyledUpload = styled(Upload)`
  .ant-upload {
    padding: 0;
  }
`;

const UploadFileList = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-04);
  border: 1px solid var(--gray-20);
  &:not(:first-child) {
    border-top: none;
  }
  height: 40px;

  .file-name {
    flex: 3;
    margin: 0 var(--spacing-04);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .file-size {
    flex: 1;
  }
  .remove-button {
    opacity: 0;
  }
  &:hover {
    .remove-button {
      opacity: 1;
    }
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    padding-bottom: 0;
  }
`;

const FilesFormItemWrapper = styled.div`
  height: 214px;
  overflow-y: auto;
`;

const StyledModal = styled(Modal)`
  .ant-modal-footer {
    padding-top: var(--spacing-05);
  }
`;

const AddMoreButton = styled(TaskFilterButton)`
  margin-right: var(--spacing-03);
`;

export interface CreateGroupFormProps {
  groupName: string;
  permission: string;
  files: UploadFile[];
}

export const FileCreateGroupModal = ({
  onSubmit,
  onClose,
  isCreatingGroup,
  folders,
}: {
  onSubmit: (form: CreateGroupFormProps) => void;
  onClose: VoidFunction;
  isCreatingGroup: boolean;
  folders?: ProjectFileGroup[];
}) => {
  const translate = useTranslate();
  const [form] = Form.useForm<CreateGroupFormProps>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const folderNames = useMemo(
    () => folders?.map((_folder) => _folder.folderName.toLowerCase()) || [],
    [folders],
  );
  const beforeUpload = () => false;
  const onChange = ({ file: currentFile, fileList: _list }) => {
    /**
     * This callback gets executed for all the files present in file list,
     * whereas we need to run the validation only once,
     * hence executing on the last iteration.
     */
    if (currentFile.uid !== _list[_list.length - 1].uid) {
      return;
    }
    let largeFileCount = 0;
    let invalidTypeCount = 0;

    const validList = _list.filter((_file) => {
      if (isEmpty(_file.type)) {
        invalidTypeCount += 1;

        return false;
      }
      if (_file.size > MAX_FILE_SIZE_LIMIT) {
        largeFileCount += 1;

        return false;
      }

      return true;
    });

    if (largeFileCount > 0) {
      toastService({
        title: translate('project.files.upload.error.title'),
        message: translate('project.files.upload.size.limit.error.message', {
          count: largeFileCount,
        }),
      }).error();
    }

    if (invalidTypeCount > 0) {
      toastService({
        title: translate('project.files.upload.error.title'),
        message: translate('project.files.upload.unknown.type.error.message', {
          count: invalidTypeCount,
        }),
      }).error();
    }

    setFileList(validList);
  };

  useEffect(() => {
    form.setFieldsValue({
      files: fileList,
    });
  }, [fileList, form]);

  const onRemove = (uid: string) => {
    setFileList([...fileList.filter((_file) => _file.uid !== uid)]);
  };

  const _onSubmit = async () => {
    const values = await form.validateFields();

    onSubmit(values);
  };

  const UploadListItem = ({ file, remove }) => {
    return (
      <UploadFileList>
        <FileExtensionIcon name={file.name} />
        <div className="file-name">{file.name}</div>
        <div className="file-size">{formatBytes(file.size, 1)}</div>
        <Button
          className="remove-button"
          type="link"
          onClick={remove}
          size="small"
          icon={<Close16 />}
        />
      </UploadFileList>
    );
  };

  return (
    <StyledModal
      visible
      onCancel={onClose}
      title={
        <ModalTitle>
          <FormattedMessage id="project.files.create.group.modal.title" />
        </ModalTitle>
      }
      footer={
        <>
          {fileList.length > 0 && (
            <StyledUpload
              beforeUpload={beforeUpload}
              onChange={onChange}
              showUploadList={false}
              fileList={fileList}
              multiple
            >
              <AddMoreButton size="middle">
                <FormattedMessage id="project.files.create.folder.footer.button.add.more" />
              </AddMoreButton>
            </StyledUpload>
          )}
          <Button
            type="primary"
            onClick={_onSubmit}
            loading={isCreatingGroup}
            data-cy="project.files.create.folder.submit.button"
          >
            <FormattedMessage id="upload" />
          </Button>
        </>
      }
    >
      <StyledForm layout="vertical" form={form} autoComplete="off">
        <Form.Item
          required
          rules={[
            { required: true, message: translate('input.required') },
            ({ isFieldTouched }) => ({
              validator: (_, value) => {
                if (!isFieldTouched('groupName')) {
                  return Promise.resolve();
                }
                if (folderNames.includes(value?.toLowerCase())) {
                  return Promise.reject(new Error(`${value} already exists`));
                }

                return Promise.resolve();
              },
            }),
          ]}
          name="groupName"
          label={
            <FormattedMessage id="project.files.create.group.name.input.label" />
          }
        >
          <Input data-cy="project.files.create.folder.name.input" />
        </Form.Item>
        <HideIfNotAllowed
          ruleName={PermissionRuleName.FilePrivateGroupCreateAction}
        >
          <Form.Item
            required
            rules={[{ required: true, message: translate('input.required') }]}
            name="permission"
            label={
              <FormattedMessage id="project.files.create.group.permission.input.label" />
            }
          >
            <Radio.Group>
              <Radio
                value="private"
                data-cy="project.files.create.folder.type.private"
              >
                <FormattedMessage id="private" />
              </Radio>
              <Radio
                value="public"
                data-cy="project.files.create.folder.type.shared"
              >
                <FormattedMessage id="public" />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </HideIfNotAllowed>
        <Form.Item
          required
          name="files"
          rules={[{ required: true, message: translate('input.required') }]}
          label={
            <FormattedMessage id="project.files.create.group.files.input.label" />
          }
        >
          <FilesFormItemWrapper>
            {fileList.length <= 0 && (
              <StyledDragger
                beforeUpload={beforeUpload}
                onChange={onChange}
                showUploadList={false}
                fileList={fileList}
                multiple
              >
                <DraggerTitle>
                  <FormattedMessage id="project.files.dragger.title" />
                </DraggerTitle>
                <DraggerOption>
                  <FormattedMessage id="project.files.dragger.optional.text" />
                </DraggerOption>
                <Button
                  icon={<Upload16 />}
                  iconPlacement="right"
                  data-cy="project.files.create.folder.upload.button"
                >
                  <FormattedMessage id="project.files.dragger.select.button" />
                </Button>
              </StyledDragger>
            )}

            {fileList.map((_file: UploadFile) => (
              <UploadListItem
                key={_file.uid}
                file={_file}
                remove={() => onRemove(_file.uid)}
              />
            ))}
          </FilesFormItemWrapper>
        </Form.Item>
      </StyledForm>
    </StyledModal>
  );
};
